import React from "react";
import styled from "styled-components";
import { router } from "@inertiajs/react";
import { ArrowLeft, Home } from "lucide-react";
import { useLanguage } from "../contexts/language";
import { useRoom } from "../contexts/room";
import { LanguageSwitcher } from "./language-switcher";
import { NotificationToggle } from "./notification-toggle";
import { unmodule } from "../util";

const headerLogo = unmodule(require("../assets/logo-dark.png"));

const HeaderWrapper = styled.header`
  position: ${(props) => (props.$sticky ? "sticky" : "relative")};
  top: 0;
  background-color: #fff;
  border-bottom: 1px solid #e5e5e5;
  z-index: 2;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.04);
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: ${(props) => props.$maxWidth || "720px"};
  margin: 0 auto;
  padding: 10px 12px;

  @media (min-width: 480px) {
    padding: 12px 16px;
  }
`;

const LeftZone = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const CenterZone = styled.div`
  flex: 1;
  text-align: center;
  min-width: 0;
  padding: 0 6px;

  @media (min-width: 480px) {
    padding: 0 8px;
  }
`;

const RightZone = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;

  @media (min-width: 480px) {
    gap: 6px;
  }
`;

const NavButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  border: none;
  background: none;
  cursor: pointer;
  color: #333;
  padding: 4px;
  font-size: 14px;
  transition: color 0.2s;

  &:hover {
    color: #c9a961;
  }
`;

const BackLabelText = styled.span`
  display: none;

  @media (min-width: 480px) {
    display: inline;
  }
`;

const Logo = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  object-fit: cover;

  @media (min-width: 480px) {
    width: 34px;
    height: 34px;
  }
`;

const Title = styled.h1`
  font-size: 13px;
  font-weight: 600;
  color: #333;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (min-width: 480px) {
    font-size: 15px;
  }
`;

const Subtitle = styled.p`
  font-size: 12px;
  color: #4b5563;
  margin: 2px 0 0 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (min-width: 480px) {
    font-size: 13px;
  }
`;

const ProfileActionLink = styled.button`
  border: none;
  background: transparent;
  color: #6b7280;
  font-size: 12px;
  cursor: pointer;
  margin-top: 2px;
  padding: 0;
  text-decoration: underline;
  transition: color 0.2s;

  &:hover {
    color: #c9a961;
  }
`;

export const PageHeader = ({
  title,
  showBackButton = true,
  backPath = null,
  backLabel = null,
  useHomeIcon = false,
  showLogo = true,
  showGuestInfo = true,
  showProfileAction = false,
  onChangeProfile = null,
  autoPromptNotification = false,
  maxWidth = "768px",
  sticky = true,
  buildingCode,
  children,
}) => {
  const { t } = useLanguage();
  const { guestName, roomNumber } = useRoom();

  const handleBack = () => {
    if (backPath) {
      router.visit(backPath);
    } else {
      window.history.back();
    }
  };

  return (
    <HeaderWrapper $sticky={sticky}>
      <TopBar $maxWidth={maxWidth}>
        <LeftZone>
          {showBackButton && (
            <NavButton onClick={handleBack}>
              {useHomeIcon ? <Home size={18} /> : <ArrowLeft size={18} />}
              {backLabel && <BackLabelText>{backLabel}</BackLabelText>}
            </NavButton>
          )}
          {showLogo && <Logo src={headerLogo} alt="HEBE" />}
        </LeftZone>

        <CenterZone>
          {title && <Title>{title}</Title>}
          {showGuestInfo && guestName && (
            <Subtitle>
              {guestName}
              {roomNumber ? ` • ${t.room.roomNumber} ${roomNumber}` : ""}
            </Subtitle>
          )}
          {showProfileAction && onChangeProfile && (
            <ProfileActionLink onClick={onChangeProfile}>
              {t.header.changeProfile}
            </ProfileActionLink>
          )}
        </CenterZone>

        <RightZone>
          <NotificationToggle autoPrompt={autoPromptNotification} />
          <LanguageSwitcher />
        </RightZone>
      </TopBar>

      {children}
    </HeaderWrapper>
  );
};
